import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  InputLabel,
  Menu,
  Stack,
  Tab,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import { KaseApi } from '@/api';
import { DomesticIncidentReportApi } from '@/api/api.domestic-incident-report';
import { IcArrowDown, IcArrowUp } from '@/assets/images';
import { FormCard, FormRoot, RenderFormField } from '@/components';
import { DATE_FORMAT } from '@/config';
import {
  IDomesticIncidentReportRecord,
  IMasterNamePerson,
  MasterNameInvolvement,
} from '@/models';
import {
  allTabsSections,
  DomesticIncidentReportTabs,
  DomesticTypeKey,
  ITabSection,
} from '@/pages/Kases/DomesticIncidentReport/data';
import { useDomesticRelatedNames } from '@/pages/Kases/DomesticIncidentReport/domestic_hook';
import {
  AppModalDomestic,
  ModalHeader,
  TabsDomesticIncident,
  TabSectionStyled,
} from '@/pages/Kases/DomesticIncidentReport/style';
import {
  addressHandler,
  dateHelper,
  dateSectionExtract,
  extractCustomAddress,
  populateSectionValues,
} from '@/pages/Kases/DomesticIncidentReport/utils';
import { MenuState } from '@/pages/MasterIndex/components/RelatedRecordsTable';
import { ConfirmTypes, useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatPhoneNumber, getMasterNameDisplayName } from '@/utils';

type FormActionType = 'REDACTED' | 'UNREDACTED' | 'BOTH' | 'SAVE';

interface DomesticIncidentReportModalProps {
  mode: 'create' | 'edit' | 'view';
  dirId?: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  kaseId?: string;
  readOnly?: boolean;
}

export const DomesticIncidentReportModal = ({
  mode,
  dirId,
  open,
  onClose,
  onSuccess,
  kaseId: initialKaseId,
  readOnly = false,
}: DomesticIncidentReportModalProps) => {
  const methods = useForm({});
  const { handleSubmit, getValues, setValue, watch, reset } = methods;
  const { updateToast } = useToastStore();
  const theme = useTheme();

  const [selectedTab, setSelectedTab] = useState<DomesticTypeKey>('incident');
  const [loading, setLoading] = useState(mode === 'edit' || mode === 'view');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_dirRecord, setDirRecord] =
    useState<IDomesticIncidentReportRecord | null>(null);
  const [linkedKaseId, setLinkedKaseId] = useState<string | undefined>(
    initialKaseId,
  );
  const [menuState, setMenuState] = useState<MenuState>({ menuEl: null });
  const [includeHelpSheet, setIncludeHelpSheet] = useState<boolean>(true);
  const [isViewMode, setIsViewMode] = useState(mode === 'view');

  const { menuEl } = menuState;
  const openMenu = Boolean(menuEl);

  // Case search state for Tools create mode
  const [caseSearchQuery, setCaseSearchQuery] = useState('');
  const [caseOptions, setCaseOptions] = useState<any[]>([]);
  const [caseSearchLoading, setCaseSearchLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>(null);
  const [incidentError, setIncidentError] = useState(false);

  const isToolsCreateMode = mode === 'create' && !initialKaseId;

  // Debounced search for available cases
  useEffect(() => {
    if (!isToolsCreateMode) return;

    const timer = setTimeout(async () => {
      setCaseSearchLoading(true);
      try {
        const response = await DomesticIncidentReportApi.availableCases(
          caseSearchQuery || undefined,
        );
        setCaseOptions(response.data || []);
      } catch {
        setCaseOptions([]);
      } finally {
        setCaseSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [caseSearchQuery, isToolsCreateMode]);

  // Handle case selection from dropdown
  const handleCaseSelect = useCallback(
    (kase: any) => {
      if (!kase) {
        setSelectedCase(null);
        setLinkedKaseId(undefined);
        return;
      }
      setSelectedCase(kase);
      setLinkedKaseId(kase._id);
      setIncidentError(false);

      const reported_date = kase.incidentReportedAt || null;
      const occured_date = kase.incidentStartedAt || null;
      const { zipName = '', zipcode = '', state = '' } = kase.address || {};

      setValue('incident.agency', kase.agency?.name);
      setValue('incident.oriNumber', kase.agency?.oriNumber);
      setValue('incident.incident', kase.number);
      setValue('incident.reported_date', reported_date);
      setValue(
        'incident.reported_time',
        reported_date ? moment(reported_date) : '',
      );
      setValue('incident.occured_date', occured_date);
      setValue(
        'incident.occured_time',
        occured_date ? moment(occured_date) : '',
      );
      setValue('incident.address', kase.address);
      setValue(
        'incident.city_state_zip',
        [zipName, zipcode, state].filter((item) => item).join(', '),
      );
    },
    [setValue],
  );

  // Fields that should be disabled based on mode and case linkage
  const disabledFields = useMemo(() => {
    const fields = new Set<string>();
    if (mode !== 'create' || initialKaseId || linkedKaseId) {
      fields.add('incident.incident');
    }
    if (initialKaseId || linkedKaseId) {
      fields.add('incident.agency');
      fields.add('incident.oriNumber');
      fields.add('incident.reported_date');
      fields.add('incident.reported_time');
      if (selectedCase?.incidentStartedAt) {
        fields.add('incident.occured_date');
        fields.add('incident.occured_time');
      }
      fields.add('incident.address');
      fields.add('incident.city_state_zip');
    }
    return fields;
  }, [mode, initialKaseId, linkedKaseId, selectedCase?.incidentStartedAt]);

  // Fetch related names when a kase is linked
  const { allOffenders, allWitnesses, allVictims, allRelatedNames } =
    useDomesticRelatedNames(linkedKaseId || '');

  watch([
    'incident.customAddress',
    'suspect_p2.customAddress',
    'closing_notes.child_victim_neglect',
    'closing_notes.is_dir_given',
    'closing_notes.is_rights_given',
    'evidence.destruction_of_property',
    'evidence.other_evidence_check',
    'evidence.photos_taken_check',
    'offense.was_arrested',
    'prior_history.beaten_while_pregnant',
    'prior_history.capable_of_killing_child',
    'prior_history.increased_severity_past_months',
    'prior_history.strangled_or_choked',
    'prior_history.threatened_to_kill_child',
    'prior_history.violently_jealous',
    'suspect_p2.dob',
    'suspect_p2.suspect_P2_relationship_victim_P1_check',
    'suspect_p2.suspect_p2_name',
    'suspect_p2.was_suspect_injured',
    'victim.dob',
    'victim.customAddress',
    'victim_interview.access_to_guns',
    'victim_interview.emo_condition_check',
    'victim_interview.fearfull_victim',
    'victim_interview.gun_check',
    'victim_interview.in_pain',
    'victim_interview.injured',
    'victim_interview.suspect_threats_to_check',
    'victim_interview.visible_marks',
    'witnesses_1.witnesses_1_name',
    'witnesses_2.witnesses_2_name',
    'witnesses_3.witnesses_3_name',
    'witnesses_1.customAddress',
    'witnesses_2.customAddress',
    'witnesses_3.customAddress',
  ]);

  // Load existing DIR record for edit/view modes, or pre-fill from kase for create mode
  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && dirId) {
      loadDirRecord(dirId);
    } else {
      // Create mode defaults
      const sectionDefaults = {
        suspect_p2: {
          live_together: false,
          suspect_present: false,
          was_suspect_injured: false,
          possible_substance_use: false,
        },
        victim_interview: {
          fearfull_victim: false,
          weapon_used: false,
          access_to_guns: false,
          injured: false,
          in_pain: false,
          visible_marks: false,
        },
        suspect: { completed_710: false },
        incident_p2: {
          dir_repository_checked: false,
          registry_checked: false,
          in_effect: false,
        },
        evidence: { destruction_of_property: false },
        offense: { offense_committed: false, was_arrested: false },
        prior_history: {
          threatened_to_kill_child: false,
          strangled_or_choked: false,
          beaten_while_pregnant: false,
          capable_of_killing_child: false,
          violently_jealous: false,
          increased_severity_past_months: false,
        },
        closing_notes: {
          child_victim_neglect: false,
          is_dir_given: false,
          is_rights_given: false,
        },
      };

      if (initialKaseId) {
        // Fetch kase data to auto-populate incident fields
        KaseApi.getOne(initialKaseId)
          .then((response) => {
            const kase = response.data;
            const reported_date = kase?.incidentReportedAt || null;
            const occured_date = kase?.incidentStartedAt || null;
            const {
              zipName = '',
              zipcode = '',
              state = '',
            } = kase?.address || {};

            reset({
              incident: {
                agency: kase?.agency?.name,
                oriNumber: kase?.agency?.oriNumber,
                incident: kase?.number,
                reported_date,
                reported_time: reported_date ? moment(reported_date) : '',
                occured_date,
                occured_time: occured_date ? moment(occured_date) : '',
                address: kase?.address,
                city_state_zip: [zipName, zipcode, state]
                  .filter((item) => item)
                  .join(', '),
              },
              ...sectionDefaults,
            });
          })
          .catch(() => {
            // Fall back to defaults without incident data
            reset(sectionDefaults);
          });
      } else {
        reset(sectionDefaults);
      }
    }
  }, [dirId, mode]);

  const loadDirRecord = async (id: string) => {
    try {
      setLoading(true);
      const response = await DomesticIncidentReportApi.getOne(id);
      const record = response.data;
      setDirRecord(record);

      if (record.kase?._id) {
        setLinkedKaseId(record.kase._id);
      }

      // Build form data from record sections
      const formData: any = {};
      const sectionKeys = [
        'incident',
        'victim',
        'suspect_p2',
        'victim_interview',
        'suspect',
        'witnesses_1',
        'witnesses_2',
        'witnesses_3',
        'incident_p2',
        'evidence',
        'offense',
        'prior_history',
        'closing_notes',
      ];

      sectionKeys.forEach((key) => {
        if (record[key as keyof IDomesticIncidentReportRecord]) {
          formData[key] = record[key as keyof IDomesticIncidentReportRecord];
        }
      });

      const customAddressData = extractCustomAddress(formData);
      reset({
        ...formData,
        ...customAddressData,
      });
    } catch {
      updateToast({
        open: true,
        message: 'Failed to load domestic incident report',
      });
    } finally {
      setLoading(false);
    }
  };

  // Watch for name field changes to auto-populate related fields
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === 'change' && name) {
        const [sectionName, inputName] = name.split('.');
        let currentTabSection, currentUser;
        switch (name) {
          case 'victim.dob':
          case 'suspect_p2.dob': {
            const dob = moment(value[sectionName]?.[inputName]);
            setValue(
              `${sectionName}.age`,
              dob && dob.isValid() ? moment().diff(dob, 'year') : '',
            );
            break;
          }
          case 'victim.victim_name':
          case 'suspect_p2.suspect_p2_name':
          case 'witnesses_1.witnesses_1_name':
          case 'witnesses_2.witnesses_2_name':
          case 'witnesses_3.witnesses_3_name': {
            currentUser = allRelatedNames.find(
              (user) => user.masterName._id === value[sectionName]?.[inputName],
            );
            currentTabSection = sectionName;
            if (
              currentUser?.masterName?.phoneNumbers &&
              currentUser?.masterName?.phoneNumbers[0]
            ) {
              setValue(
                `${sectionName}.phone`,
                formatPhoneNumber(
                  currentUser?.masterName?.phoneNumbers[0]?.phoneNumber,
                ),
              );
            } else {
              setValue(`${sectionName}.phone`, '');
            }
            break;
          }
        }

        if (!currentUser) return;

        const currentMasterName = currentUser.masterName as IMasterNamePerson;
        setValue(`${currentTabSection}.dob`, currentMasterName.birthday);
        if (currentMasterName?.addresses) {
          setValue(
            `${currentTabSection}.address`,
            currentMasterName?.addresses[0] || null,
          );
          const { zipName, zipcode, state } =
            currentMasterName.addresses[0] || {};
          setValue(
            `${currentTabSection}.city_state_zip`,
            [zipName, zipcode, state].filter((item) => item).join(', '),
          );
        }

        const involvementValue = currentUser.relationship?.involvement;
        const involvement = Array.isArray(involvementValue)
          ? involvementValue
          : involvementValue
          ? [involvementValue]
          : [];
        if (!involvement.includes(MasterNameInvolvement.WITNESS)) {
          const dob = moment(currentMasterName.birthday);
          setValue(`${currentTabSection}.gender`, currentMasterName.sex);
          setValue(
            `${currentTabSection}.age`,
            dob && dob.isValid() ? moment().diff(dob, 'year') : '',
          );
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, allRelatedNames]);

  const handleTabChange = (event: React.SyntheticEvent) => {
    setSelectedTab(event.currentTarget.id as DomesticTypeKey);
  };

  const handleMenuClose = () => {
    setMenuState({ menuEl: null });
  };

  const onIncludeHelpSheetToggle = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setIncludeHelpSheet((prev) => !prev);
  };

  const getItems = {
    offendersList: allOffenders.map((offender) => ({
      value: offender.masterName._id,
      label: getMasterNameDisplayName(offender.masterName),
    })),
    witnessesList: allWitnesses.map((witness) => {
      const selectedWitnesses = [
        getValues('witnesses_1')?.witnesses_1_name,
        getValues('witnesses_2')?.witnesses_2_name,
        getValues('witnesses_3')?.witnesses_3_name,
      ];
      const label = getMasterNameDisplayName(witness.masterName);
      return {
        value: witness.masterName._id,
        label,
        disabled: selectedWitnesses.includes(label),
      };
    }),
    victimsList: allVictims.map((victim) => ({
      value: victim.masterName._id,
      label: getMasterNameDisplayName(victim.masterName),
    })),
  };

  const buildPrintData = (values: any, submitter: FormActionType) => {
    return {
      incident: {
        ...dateSectionExtract(values.incident),
        ...addressHandler(values.incident, submitter as any),
        reported_date: dateHelper(values.incident?.reported_date),
        occured_date: dateHelper(values.incident?.occured_date),
      },
      ...(values.victim &&
        values.victim?.victim_name && {
          victim: {
            victim_name: getItems.victimsList.find(
              (v) => v.value === values?.victim?.victim_name,
            )?.label,
            age: values?.victim?.age,
            gender: values?.victim?.gender,
            language: values?.victim?.language,
            how_to_contact: values?.victim?.how_to_contact,
            ...addressHandler(values.victim, submitter as any),
            dob: dateHelper(values?.victim?.dob),
          },
        }),
      ...(values.suspect_p2 && {
        suspect_p2: {
          ...dateSectionExtract(values.suspect_p2),
          ...addressHandler(values.suspect_p2, submitter as any),
          dob: values.suspect_p2?.dob
            ? moment(values.suspect_p2.dob).format(DATE_FORMAT)
            : '',
          suspect_p2_name: getItems.offendersList.find(
            (v) => v.value === values?.suspect_p2?.suspect_p2_name,
          )?.label,
        },
      }),
      ...(values.witnesses_1 && {
        witnesses_1: {
          ...dateSectionExtract(values.witnesses_1),
          ...addressHandler(values.witnesses_1, submitter as any),
          dob: dateHelper(values.witnesses_1?.dob),
          phone: formatPhoneNumber(values.witnesses_1?.phone),
          witnesses_1_name: getItems.witnessesList.find(
            (v) => v.value === values?.witnesses_1?.witnesses_1_name,
          )?.label,
        },
      }),
      ...(values.witnesses_2 && {
        witnesses_2: {
          ...dateSectionExtract(values.witnesses_2),
          ...addressHandler(values.witnesses_2, submitter as any),
          dob: dateHelper(values.witnesses_2?.dob),
          phone: formatPhoneNumber(values.witnesses_2?.phone),
          witnesses_2_name: getItems.witnessesList.find(
            (v) => v.value === values?.witnesses_2?.witnesses_2_name,
          )?.label,
        },
      }),
      ...(values.witnesses_3 && {
        witnesses_3: {
          ...dateSectionExtract(values.witnesses_3),
          ...addressHandler(values.witnesses_3, submitter as any),
          dob: dateHelper(values.witnesses_3?.dob),
          phone: formatPhoneNumber(values.witnesses_3?.phone),
          witnesses_3_name: getItems.witnessesList.find(
            (v) => v.value === values?.witnesses_3?.witnesses_3_name,
          )?.label,
        },
      }),
      ...(values.prior_history && {
        prior_history: {
          ...values.prior_history,
          agency: values.incident?.agency,
          oriNumber: values.incident?.oriNumber,
          incident: values.incident?.incident,
          complaint: values.incident?.complaint,
        },
      }),
      ...populateSectionValues(values),
    };
  };

  const onSubmit: SubmitHandler<any> = async (values: any, event: any) => {
    if (isToolsCreateMode && !linkedKaseId) {
      setIncidentError(true);
      setSelectedTab('incident');
      return;
    }

    const nativeEvent = event?.nativeEvent as SubmitEvent;
    const submitter =
      (nativeEvent?.target as HTMLButtonElement)?.value || 'SAVE';

    try {
      if (submitter === 'SAVE') {
        // Build save payload with raw form values for draft-like save
        const saveData: Record<string, unknown> = {
          ...(linkedKaseId && { kaseId: linkedKaseId }),
        };

        const sectionKeys = [
          'incident',
          'victim',
          'suspect_p2',
          'victim_interview',
          'suspect',
          'witnesses_1',
          'witnesses_2',
          'witnesses_3',
          'incident_p2',
          'evidence',
          'offense',
          'prior_history',
          'closing_notes',
        ];

        sectionKeys.forEach((key) => {
          if (values[key]) {
            saveData[key] = values[key];
          }
        });

        if (mode === 'create') {
          await DomesticIncidentReportApi.create(saveData);
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: 'Domestic incident report created successfully',
          });
        } else if (dirId) {
          await DomesticIncidentReportApi.update(dirId, saveData);
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: 'Domestic incident report updated successfully',
          });
        }
        onSuccess();
        onClose();
        return;
      }

      // Print flow
      if (!dirId) {
        updateToast({
          open: true,
          message: 'Please save the report before printing',
        });
        return;
      }

      const dataToPrint = buildPrintData(values, submitter as FormActionType);
      const response = await DomesticIncidentReportApi.print(dirId, {
        ...dataToPrint,
        action: submitter,
        includeHelpSheet,
      });

      if (response.data) {
        const pdfUrl = URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' }),
        );
        const newWindow = window.open('', '_blank');
        const iframe = document.createElement('iframe');
        iframe.src = pdfUrl;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.style.border = 'none';
        iframe.onload = function () {
          setTimeout(function () {
            iframe.focus();
            iframe?.contentWindow?.print();
          }, 1000);
        };
        newWindow?.document?.body.appendChild(iframe);

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Domestic incident report saved to case successfully!',
        });
        onSuccess();
        onClose();
      }
    } catch (e) {
      updateToast({
        message: `Error: ${e}`,
        open: true,
      });
    }
  };

  const getTitle = () => {
    if (isViewMode) return 'Domestic Incident Report Details';
    if (mode === 'edit') return 'Edit Domestic Incident Report';
    return 'Create Domestic Incident Report';
  };

  const renderFormCard = (tabSection: ITabSection) => {
    return (
      <FormCard
        key={tabSection?.key}
        title={tabSection.title}
        sx={{
          overflow: 'visible',
          '& hr.MuiDivider-root.divider': { margin: 0 },
          '& .cardTitle': {
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        {tabSection.items.map((itemProps: any) => {
          const { grid, subItems, shouldDisplayCallback, ...inputProps } =
            itemProps;
          let shouldRenderField = true;
          if (
            shouldDisplayCallback &&
            typeof shouldDisplayCallback === 'function'
          ) {
            shouldRenderField = shouldDisplayCallback(getValues());
          }
          return (
            shouldRenderField && (
              <Grid
                key={inputProps.name}
                xs={12}
                sm={6}
                md={3}
                {...grid}
                item
                sx={{ alignContent: 'center' }}
              >
                {inputProps.type === 'subtitle' ? (
                  <>
                    <Divider />
                    <Typography variant="subtitle2" sx={{ my: 1 }}>
                      {inputProps.label}
                    </Typography>
                  </>
                ) : (
                  <>
                    {inputProps.name === 'incident.incident' &&
                    isToolsCreateMode ? (
                      <Box
                        sx={{
                          width: '100%',
                          mb: 1,
                          '& .MuiAutocomplete-root': { width: '100%', p: 0 },
                          '& .MuiAutocomplete-root .MuiTextField-root': {
                            height: 'initial',
                          },
                        }}
                      >
                        <InputLabel>Incident #</InputLabel>
                        <Autocomplete
                          options={caseOptions}
                          value={selectedCase}
                          getOptionLabel={(option) =>
                            option?.number
                              ? `${option.number}${
                                  option.agency?.name
                                    ? ` - ${option.agency.name}`
                                    : ''
                                }`
                              : ''
                          }
                          isOptionEqualToValue={(option, value) =>
                            option?._id === value?._id
                          }
                          onInputChange={(_, value) =>
                            setCaseSearchQuery(value)
                          }
                          onChange={(_, value) => handleCaseSelect(value)}
                          loading={caseSearchLoading}
                          noOptionsText="No cases found"
                          slotProps={{
                            paper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  fontSize: '0.8125rem',
                                },
                                '& .MuiAutocomplete-noOptions': {
                                  fontSize: '0.8125rem',
                                },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search case number..."
                              error={incidentError}
                              helperText={
                                incidentError
                                  ? 'Incident # is required'
                                  : undefined
                              }
                              sx={{
                                height: 42,
                                '& .MuiInputBase-root': {
                                  height: 'initial',
                                  padding: '2px 60px 2px 5px',
                                },
                                '& .MuiOutlinedInput-notchedOutline legend': {
                                  display: 'none',
                                },
                                '& .MuiInputLabel-root': { display: 'none' },
                              }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {caseSearchLoading ? (
                                      <CircularProgress size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>
                    ) : (
                      <RenderFormField
                        {...inputProps}
                        {...(inputProps.type === 'select' &&
                          typeof inputProps.items === 'string' && {
                            items:
                              getItems[
                                inputProps.items as keyof typeof getItems
                              ],
                          })}
                        disabled={
                          isViewMode ||
                          readOnly ||
                          disabledFields.has(inputProps.name)
                        }
                      />
                    )}
                    {subItems?.map((subItem: any) => {
                      const { isVisible, ...subItemProps } = subItem;
                      return (
                        isVisible(getValues(inputProps.name)) && (
                          <RenderFormField
                            {...subItemProps}
                            disabled={isViewMode || readOnly}
                          />
                        )
                      );
                    })}
                  </>
                )}
              </Grid>
            )
          );
        })}
      </FormCard>
    );
  };

  const renderContent = (rest: ITabSection) =>
    rest.cards?.map((tabSection: ITabSection) => renderFormCard(tabSection));

  return (
    <AppModalDomestic
      open={open}
      disableEscapeKeyDown={true}
      onClose={onClose}
      sx={{
        '& .modal-content': {},
        '& .btn-close': {
          margin: '-20px -20px 5px auto',
          position: 'relative',
          zIndex: 5,
        },
      }}
    >
      <Stack
        sx={{
          minWidth: 1360,
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <FormProvider {...methods}>
          <ModalHeader>
            <Typography className="title" component="h2">
              {getTitle()}
            </Typography>
            <Stack
              flexDirection="row"
              columnGap={2}
              sx={{ marginRight: '15px' }}
            >
              {isViewMode ? (
                <>
                  <Button variant="contained" color="inherit" onClick={onClose}>
                    Close
                  </Button>
                  {!readOnly && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setIsViewMode(false)}
                    >
                      Edit
                    </Button>
                  )}
                </>
              ) : initialKaseId ? (
                <>
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={handleSubmit(onSubmit)}
                    value="SAVE"
                    name="action"
                  >
                    Save as draft
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={(event) =>
                      setMenuState({ menuEl: event.currentTarget })
                    }
                  >
                    Print
                    {openMenu ? (
                      <IcArrowUp style={{ marginLeft: 10 }} />
                    ) : (
                      <IcArrowDown style={{ marginLeft: 10 }} />
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="contained" color="inherit" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleSubmit(onSubmit)}
                    value="SAVE"
                    name="action"
                  >
                    Save
                  </Button>
                </>
              )}
            </Stack>
          </ModalHeader>
          <FormRoot
            onSubmit={handleSubmit(onSubmit)}
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              margin: '0 -20px -20px',
              padding: 0,
              width: 'auto',
            }}
          >
            <TabsDomesticIncident
              orientation="vertical"
              variant="scrollable"
              value={selectedTab}
              onChange={handleTabChange}
              TabIndicatorProps={{
                style: {
                  background: colors.error.main,
                  width: '100%',
                  zIndex: 1,
                },
              }}
            >
              {Object.entries(DomesticIncidentReportTabs).map(
                ([key, label]) => (
                  <Tab
                    label={label}
                    id={key}
                    key={key}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      position: 'relative',
                      zIndex: 2,
                      marginLeft: 2,
                      color: '#fff',
                    }}
                    value={key}
                  />
                ),
              )}
            </TabsDomesticIncident>
            {loading ? (
              <TabSectionStyled
                display="flex"
                role="tabpanel"
                flexDirection="column"
                sx={{ alignItems: 'center', justifyContent: 'center' }}
              >
                <CircularProgress />
              </TabSectionStyled>
            ) : (
              allTabsSections.map((tabSection: any, index) => {
                return (
                  selectedTab === tabSection.key && (
                    <TabSectionStyled
                      key={tabSection.key}
                      display="block"
                      role="tabpanel"
                      id={`vertical-tabpanel-${index}`}
                      flexDirection="column"
                    >
                      {tabSection?.type === 'multipleCards'
                        ? renderContent(tabSection)
                        : renderFormCard(tabSection)}
                    </TabSectionStyled>
                  )
                );
              })
            )}
            <Menu
              id="print-menu"
              sx={{ flexWrap: 'nowrap', flexDirection: 'row' }}
              anchorEl={menuEl}
              open={openMenu}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{
                'aria-labelledby': 'print-button',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                onClick={onIncludeHelpSheetToggle}
                sx={{
                  cursor: 'pointer',
                  ':hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Checkbox checked={includeHelpSheet} />
                <Typography color="inherit" fontSize="15px" fontWeight="500">
                  Include Help Sheet
                </Typography>
              </Box>
              <Divider />
              <Button
                type="submit"
                color="inherit"
                sx={{ display: 'flex' }}
                onClick={handleSubmit(onSubmit)}
                value="REDACTED"
                name="action"
              >
                Redacted
              </Button>
              <Button
                type="submit"
                color="inherit"
                sx={{ display: 'flex' }}
                onClick={handleSubmit(onSubmit)}
                value="UNREDACTED"
                name="action"
              >
                Unredacted
              </Button>
            </Menu>
          </FormRoot>
        </FormProvider>
      </Stack>
    </AppModalDomestic>
  );
};
